import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  LinearProgress,
  Alert,
  Paper,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  Tag as TagIcon,
  People as PeopleIcon,
  CalendarToday as CalendarIcon,
  Inventory as InventoryIcon,
  LocalShipping as ShippingIcon,
  AttachMoney as MoneyIcon,
  Info as InfoIcon,
  Description as DocumentIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import correlationService, {
  ComprehensiveCorrelationResponse,
  DocumentPairCorrelation,
  CorrelationCriterion,
} from '../../features/cross-document/correlationService';

interface CorrelationFeedbackPanelProps {
  validationResult: ComprehensiveCorrelationResponse;
}

export const CorrelationFeedbackPanel: React.FC<CorrelationFeedbackPanelProps> = ({ validationResult }) => {
  const [expandedSection, setExpandedSection] = useState<string | false>('correlated-groups');

  const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedSection(isExpanded ? panel : false);
  };

  const getCriterionIcon = (criterion: string) => {
    switch (criterion) {
      case 'reference_number':
        return <TagIcon fontSize="small" />;
      case 'party_match':
        return <PeopleIcon fontSize="small" />;
      case 'date_proximity':
        return <CalendarIcon fontSize="small" />;
      case 'product_match':
        return <InventoryIcon fontSize="small" />;
      case 'transport_match':
        return <ShippingIcon fontSize="small" />;
      case 'value_match':
        return <MoneyIcon fontSize="small" />;
      default:
        return <InfoIcon fontSize="small" />;
    }
  };

  const renderCriterion = (criterion: CorrelationCriterion, matched: boolean) => {
    const weight = correlationService.toPercentage(criterion.weight);

    return (
      <ListItem
        key={criterion.criterion}
        sx={{
          backgroundColor: matched ? '#E8F5E9' : '#FFEBEE',
          borderRadius: 1,
          mb: 1,
        }}
      >
        <ListItemIcon>
          {matched ? (
            <CheckCircleIcon sx={{ color: '#4CAF50' }} />
          ) : (
            <CancelIcon sx={{ color: '#F44336' }} />
          )}
        </ListItemIcon>

        <ListItemText
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {getCriterionIcon(criterion.criterion)}
              <Typography variant="subtitle2" fontWeight="bold">
                {correlationService.formatCriterionName(criterion.criterion)}
              </Typography>
              <Chip label={`${weight}% weight`} size="small" variant="outlined" />
            </Box>
          }
          secondary={
            <Box sx={{ mt: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                {criterion.details}
              </Typography>
              {criterion.value && (
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                  Value: {typeof criterion.value === 'object' ? JSON.stringify(criterion.value) : criterion.value}
                </Typography>
              )}
            </Box>
          }
        />
      </ListItem>
    );
  };

  const renderDocumentPair = (pair: DocumentPairCorrelation, index: number) => {
    const confidenceLabel = correlationService.getConfidenceLabel(pair.confidence);

    return (
      <Accordion key={`pair-${index}`} sx={{ mb: 1 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
            {pair.correlated ? (
              <CheckCircleIcon sx={{ color: '#4CAF50' }} />
            ) : (
              <WarningIcon sx={{ color: '#FF9800' }} />
            )}

            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2">
                <strong>{pair.doc_a.filename}</strong> ↔ <strong>{pair.doc_b.filename}</strong>
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                <Chip
                  label={`${correlationService.toPercentage(pair.confidence)}% confidence`}
                  size="small"
                  color={confidenceLabel.severity}
                />
                <Chip label={pair.relationship_type.replace('_', ' ')} size="small" variant="outlined" />
              </Box>
            </Box>
          </Box>
        </AccordionSummary>

        <AccordionDetails>
          {/* Matching criteria */}
          {pair.matching_criteria.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, color: '#4CAF50' }}>
                ✓ Matching Criteria ({pair.matching_criteria.length})
              </Typography>
              <List dense>{pair.matching_criteria.map((criterion) => renderCriterion(criterion, true))}</List>
            </Box>
          )}

          {/* Non-matching criteria */}
          {pair.non_matching_criteria.length > 0 && (
            <Box>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, color: '#F44336' }}>
                ✗ Non-Matching Criteria ({pair.non_matching_criteria.length})
              </Typography>
              <List dense>{pair.non_matching_criteria.map((criterion) => renderCriterion(criterion, false))}</List>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <Card elevation={2} sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Correlation Details
        </Typography>

        {/* Correlated Groups */}
        <Accordion
          expanded={expandedSection === 'correlated-groups'}
          onChange={handleAccordionChange('correlated-groups')}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: '#F5F5F5' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LinkIcon />
              <Typography variant="subtitle1" fontWeight="bold">
                Correlated Groups ({validationResult.correlated_groups.length})
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {validationResult.correlated_groups.length === 0 ? (
              <Alert severity="warning">No correlated document groups found</Alert>
            ) : (
              validationResult.correlated_groups.map((group, index) => {
                const confidenceLabel = correlationService.getConfidenceLabel(group.confidence);

                return (
                  <Paper key={group.group_id} elevation={1} sx={{ p: 2, mb: 2, backgroundColor: '#FAFAFA' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Group {index + 1}: {group.relationship_type.replace('_', ' ')}
                      </Typography>
                      <Chip
                        label={`${correlationService.toPercentage(group.confidence)}% confidence`}
                        size="small"
                        color={confidenceLabel.severity}
                      />
                    </Box>

                    <Box sx={{ pl: 2 }}>
                      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                        {group.document_count} documents in this group:
                      </Typography>
                      <List dense>
                        {group.document_filenames.map((filename, idx) => (
                          <ListItem key={idx} sx={{ py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 32 }}>
                              <DocumentIcon fontSize="small" color="primary" />
                            </ListItemIcon>
                            <ListItemText primary={filename} />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  </Paper>
                );
              })
            )}
          </AccordionDetails>
        </Accordion>

        {/* Uncorrelated Documents */}
        {validationResult.uncorrelated_documents.length > 0 && (
          <Accordion
            expanded={expandedSection === 'uncorrelated'}
            onChange={handleAccordionChange('uncorrelated')}
            sx={{ mt: 1 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: '#FFEBEE' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WarningIcon sx={{ color: '#F44336' }} />
                <Typography variant="subtitle1" fontWeight="bold">
                  Uncorrelated Documents ({validationResult.uncorrelated_documents.length})
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Alert severity="error" sx={{ mb: 2 }}>
                The following documents do not correlate with any other documents in the set. They may belong to a
                different shipment or contain incorrect data.
              </Alert>

              {validationResult.uncorrelated_documents.map((doc, index) => (
                <Paper key={doc.document_id} elevation={1} sx={{ p: 2, mb: 2, borderLeft: '4px solid #F44336' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <DocumentIcon color="error" />
                    <Typography variant="subtitle2" fontWeight="bold">
                      {doc.filename}
                    </Typography>
                    <Chip label={doc.document_type} size="small" variant="outlined" />
                  </Box>

                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Reasons why this document doesn't correlate:
                  </Typography>
                  <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                    {doc.reasons.map((reason, idx) => (
                      <li key={idx}>
                        <Typography variant="body2">{reason}</Typography>
                      </li>
                    ))}
                  </ul>
                </Paper>
              ))}
            </AccordionDetails>
          </Accordion>
        )}

        {/* Pair Correlations */}
        <Accordion
          expanded={expandedSection === 'pair-correlations'}
          onChange={handleAccordionChange('pair-correlations')}
          sx={{ mt: 1 }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: '#F5F5F5' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <InfoIcon />
              <Typography variant="subtitle1" fontWeight="bold">
                Pairwise Correlations ({validationResult.correlation_details.pair_correlations.length})
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Detailed analysis of how each document pair correlates based on 6 weighted criteria.
            </Typography>

            {validationResult.correlation_details.pair_correlations.map((pair, index) => renderDocumentPair(pair, index))}
          </AccordionDetails>
        </Accordion>

        {/* Overall Stats */}
        <Box sx={{ mt: 2, p: 2, backgroundColor: '#F5F5F5', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
            Overall Correlation Statistics
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 1 }}>
            <Box>
              <Typography variant="body2" fontWeight="bold">
                {validationResult.correlation_details.correlated_pairs_count}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Correlated Pairs
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" fontWeight="bold">
                {validationResult.correlation_details.total_pairs_count}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Total Pairs Checked
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" fontWeight="bold" color={validationResult.uncorrelated_documents.length > 0 ? 'error' : 'success'}>
                {validationResult.uncorrelated_documents.length}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Uncorrelated Docs
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" fontWeight="bold">
                {correlationService.toPercentage(validationResult.confidence)}%
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Overall Confidence
              </Typography>
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default CorrelationFeedbackPanel;
